// What surrounds a tripping cat in the world: translucent catnip leaves and colour ribbons swirling
// round it, a curtain of light rising from its lane, and a puff of colour where it lands.
// Colours follow the site's psy.gif (red, cyan, lime), made as shaders, not copied.
import {
  AdditiveBlending, BufferAttribute, BufferGeometry, CatmullRomCurve3, DoubleSide, Group,
  InstancedMesh, Matrix4, Mesh, PlaneGeometry, Points, Quaternion, ShaderMaterial, TubeGeometry, Vector3,
} from 'three';
import { LANE_WIDTH } from '../../data/track';
import { createLeafGeometry } from '../cats/props';

const PSY = /* glsl */ `
vec3 psy(float t) {
  // red → cyan → lime, cycling
  vec3 a = vec3(1.35, 0.12, 0.28), b = vec3(0.1, 1.15, 1.3), c = vec3(0.55, 1.35, 0.12);
  float k = fract(t);
  return k < 0.333 ? mix(a, b, k * 3.0) : k < 0.666 ? mix(b, c, (k - 0.333) * 3.0) : mix(c, a, (k - 0.666) * 3.0);
}`;

const LEAVES = 36;
const _m = new Matrix4();
const _q = new Quaternion();
const _p = new Vector3();
const _s = new Vector3();
const _e = new Vector3();

/** Leaves and ribbons orbiting the tripping cat. */
export class TripAura {
  readonly group = new Group();
  private leaves: InstancedMesh;
  private leafMat: ShaderMaterial;
  private ribbons: Mesh[] = [];
  private ribbonMat: ShaderMaterial;
  private seeds: { r: number; h: number; speed: number; phase: number; tilt: number; size: number }[] = [];
  /** Camera distance to the cat: leaves and ribbons nearer the camera than the cat fade out. */
  private uCatDepth = { value: 1e4 };

  constructor() {
    this.group.name = 'trip-aura';
    this.group.visible = false;
    this.leafMat = new ShaderMaterial({
      uniforms: { uTime: { value: 0 }, uFade: { value: 0 }, uCatDepth: this.uCatDepth },
      vertexShader: /* glsl */ `
        varying vec2 vUv; varying float vSeed; varying float vDepth;
        void main() {
          vUv = uv; vSeed = float(gl_InstanceID) * 0.137;
          vec4 mv = modelViewMatrix * instanceMatrix * vec4(position, 1.0);
          vDepth = -mv.z;
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        uniform float uTime; uniform float uFade; uniform float uCatDepth;
        varying vec2 vUv; varying float vSeed; varying float vDepth;
        ${PSY}
        void main() {
        // nothing of the aura may sit between the camera and the cat: what is in front of it fades
        float front = smoothstep(uCatDepth - 1.4, uCatDepth - 0.25, vDepth);
          float vein = smoothstep(0.03, 0.0, abs(vUv.y - 0.5)) + smoothstep(0.04, 0.0, abs(fract(vUv.x * 9.0 + abs(vUv.y - 0.5) * 3.0) - 0.5) - 0.44);
          vec3 c = mix(vec3(0.35, 1.0, 0.25), psy(uTime * 0.25 + vSeed), 0.55) * (0.6 + vein * 0.9);
          gl_FragColor = vec4(c * uFade * front, 0.55 * uFade * front);
        }`,
      transparent: true, depthWrite: false, blending: AdditiveBlending, side: DoubleSide,
    });
    this.leaves = new InstancedMesh(createLeafGeometry(0.22), this.leafMat, LEAVES);
    this.leaves.frustumCulled = false;
    for (let i = 0; i < LEAVES; i++) {
      this.seeds.push({ r: 0.7 + ((i * 0.618) % 1) * 2.2, h: -1.2 + ((i * 0.377) % 1) * 2.8, speed: 0.25 + ((i * 0.29) % 1) * 0.6, phase: i * 2.399, tilt: (i * 0.71) % 1, size: 0.7 + ((i * 0.53) % 1) * 1.1 });
    }
    this.group.add(this.leaves);

    this.ribbonMat = new ShaderMaterial({
      uniforms: { uTime: { value: 0 }, uFade: { value: 0 }, uCatDepth: this.uCatDepth },
      vertexShader: 'varying vec2 vUv; varying float vDepth; void main(){ vUv = uv; vec4 mv = modelViewMatrix * vec4(position, 1.0); vDepth = -mv.z; gl_Position = projectionMatrix * mv; }',
      fragmentShader: /* glsl */ `
        uniform float uTime; uniform float uFade; uniform float uCatDepth; varying vec2 vUv; varying float vDepth;
        ${PSY}
        void main() {
        // nothing of the aura may sit between the camera and the cat: what is in front of it fades
        float front = smoothstep(uCatDepth - 1.4, uCatDepth - 0.25, vDepth);
          float along = vUv.x;
          float flow = fract(along * 3.0 - uTime * 0.6);
          float a = smoothstep(0.0, 0.15, along) * smoothstep(1.0, 0.8, along) * (0.35 + 0.65 * smoothstep(0.3, 0.0, abs(flow - 0.5)));
          a *= front;
          gl_FragColor = vec4(psy(along * 1.5 - uTime * 0.3) * a * uFade, a * uFade);
        }`,
      transparent: true, depthWrite: false, blending: AdditiveBlending, side: DoubleSide,
    });
    for (let k = 0; k < 4; k++) {
      const pts: Vector3[] = [];
      for (let i = 0; i <= 40; i++) {
        const t = i / 40;
        const a = t * Math.PI * 4 + k * 1.57;
        const r = 1.1 + 0.6 * Math.sin(t * 6 + k);
        pts.push(new Vector3(Math.cos(a) * r, (t - 0.5) * 3 + Math.sin(a * 0.5) * 0.4, Math.sin(a) * r));
      }
      const m = new Mesh(new TubeGeometry(new CatmullRomCurve3(pts), 160, 0.035 + k * 0.01, 6, false), this.ribbonMat);
      m.frustumCulled = false;
      this.ribbons.push(m);
      this.group.add(m);
    }
  }

  /** `strength` 0..1; `centre` is the cat's body; `catDepth` the camera's distance to it along its view. */
  update(strength: number, centre: Vector3, timeS: number, catDepth = 1e4): void {
    this.group.visible = strength > 0.01;
    if (!this.group.visible) return;
    this.uCatDepth.value = catDepth;
    this.group.position.copy(centre);
    this.leafMat.uniforms.uTime!.value = timeS;
    this.leafMat.uniforms.uFade!.value = strength;
    this.ribbonMat.uniforms.uTime!.value = timeS;
    this.ribbonMat.uniforms.uFade!.value = strength * 0.9;
    for (let i = 0; i < LEAVES; i++) {
      const s = this.seeds[i]!;
      const a = s.phase + timeS * s.speed;
      const r = s.r * (0.6 + 0.4 * strength);
      _p.set(Math.cos(a) * r, s.h + Math.sin(timeS * 0.8 + s.phase) * 0.3, Math.sin(a) * r);
      _e.set(timeS * (0.5 + s.tilt), a + s.tilt * 3, timeS * 0.7 * s.tilt);
      _q.setFromAxisAngle(_s.set(Math.sin(_e.x), Math.cos(_e.z), Math.sin(_e.y)).normalize(), timeS * (0.6 + s.tilt) + s.phase);
      _s.setScalar(s.size * strength);
      this.leaves.setMatrixAt(i, _m.compose(_p, _q, _s));
    }
    this.leaves.instanceMatrix.needsUpdate = true;
    this.ribbons.forEach((m, k) => { m.rotation.y = timeS * (0.35 + k * 0.12) * (k % 2 ? -1 : 1); m.rotation.x = Math.sin(timeS * 0.3 + k) * 0.3; });
  }
}

/** A curtain of light rising from a tripping cat's lane, fading upwards. */
export class LaneCurtain {
  readonly mesh: Mesh;
  private mat: ShaderMaterial;

  constructor() {
    this.mat = new ShaderMaterial({
      uniforms: { uTime: { value: 0 }, uFade: { value: 0 } },
      vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: /* glsl */ `
        uniform float uTime; uniform float uFade; varying vec2 vUv;
        ${PSY}
        void main() {
          float up = vUv.y;
          float streaks = 0.5 + 0.5 * sin(vUv.x * 160.0 + sin(vUv.x * 23.0 + uTime) * 3.0 - uTime * 2.0);
          float ends = smoothstep(0.0, 0.2, vUv.x) * smoothstep(1.0, 0.8, vUv.x);
          float a = pow(1.0 - up, 2.2) * (0.35 + 0.65 * streaks) * ends;
          vec3 c = psy(vUv.x * 2.0 + up * 0.6 - uTime * 0.35);
          gl_FragColor = vec4(c * a * uFade * 0.8, a * uFade);
        }`,
      transparent: true, depthWrite: false, blending: AdditiveBlending, side: DoubleSide,
    });
    // two crossed sheets along the lane: 70 m long, 5 m tall
    const g = new PlaneGeometry(70, 5);
    g.translate(0, 2.5, 0);
    this.mesh = new Mesh(g, this.mat);
    const cross = new Mesh(g, this.mat);
    cross.rotation.x = Math.PI / 2;
    cross.scale.set(1, LANE_WIDTH / 5 * 0.9, 1);
    cross.position.y = 0.02;
    this.mesh.add(cross);
    this.mesh.name = 'trip-lane-curtain';
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
  }

  update(strength: number, x: number, z: number, timeS: number): void {
    this.mesh.visible = strength > 0.01;
    if (!this.mesh.visible) return;
    this.mesh.position.set(x, 0, z);
    this.mat.uniforms.uTime!.value = timeS;
    this.mat.uniforms.uFade!.value = strength;
  }
}

const PUFF = 320;
const PUFF_LIFE = 1.6;

/** A soft burst of coloured motes where a tripping cat touches down. */
export class LandingPuff {
  readonly points: Points;
  private mat: ShaderMaterial;
  private age = PUFF_LIFE;

  constructor() {
    const g = new BufferGeometry();
    const dir = new Float32Array(PUFF * 3);
    const seed = new Float32Array(PUFF);
    for (let i = 0; i < PUFF; i++) {
      const a = (i * 2.399) % (Math.PI * 2);
      const up = 0.15 + ((i * 0.618) % 1) * 0.9;
      const sp = 0.6 + ((i * 0.377) % 1) * 1.6;
      dir[i * 3] = Math.cos(a) * sp; dir[i * 3 + 1] = up * sp * 0.9; dir[i * 3 + 2] = Math.sin(a) * sp;
      seed[i] = (i * 0.137) % 1;
    }
    g.setAttribute('position', new BufferAttribute(new Float32Array(PUFF * 3), 3));
    g.setAttribute('dir', new BufferAttribute(dir, 3));
    g.setAttribute('seed', new BufferAttribute(seed, 1));
    this.mat = new ShaderMaterial({
      uniforms: { uAge: { value: PUFF_LIFE }, uLife: { value: PUFF_LIFE }, uScale: { value: 300 } },
      vertexShader: /* glsl */ `
        attribute vec3 dir; attribute float seed;
        uniform float uAge; uniform float uLife; uniform float uScale;
        varying float vSeed; varying float vLife;
        void main() {
          float t = uAge;
          vec3 p = dir * (1.0 - exp(-t * 2.6)) * 1.3 + vec3(0.0, -0.4 * t * t, 0.0);
          p.y = max(p.y, 0.02);
          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          vSeed = seed; vLife = clamp(t / uLife, 0.0, 1.0);
          gl_PointSize = uScale * (0.05 + 0.07 * seed) * (1.0 - vLife * 0.5) / -mv.z;
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        varying float vSeed; varying float vLife;
        ${PSY}
        void main() {
          float d = length(gl_PointCoord - 0.5);
          float a = smoothstep(0.5, 0.0, d) * (1.0 - vLife) * (1.0 - vLife);
          gl_FragColor = vec4(psy(vSeed + vLife * 0.7) * a * 1.6, a);
        }`,
      transparent: true, depthWrite: false, blending: AdditiveBlending,
    });
    this.points = new Points(g, this.mat);
    this.points.frustumCulled = false;
    this.points.visible = false;
    this.points.name = 'trip-landing-puff';
  }

  burst(x: number, z: number): void {
    this.points.position.set(x, 0.05, z);
    this.age = 0;
    this.points.visible = true;
  }

  update(dt: number, viewportHeightPx: number): void {
    if (this.age >= PUFF_LIFE) { this.points.visible = false; return; }
    this.age += dt;
    this.mat.uniforms.uAge!.value = this.age;
    this.mat.uniforms.uScale!.value = viewportHeightPx;
  }
}
